import { useMutation } from '@apollo/client';
import { GitHubAppInstallQueryField } from '@common/neo.tax';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  CREATE_GITHUB_APP_INSTALL_URL,
  CreateGitHubAppInstallUrlMutation,
  CreateGitHubAppInstallUrlMutationVariables,
} from '../graphql';
import { useCurrentCompanyId } from './useCurrentCompanyId';
import { useCurrentUser } from './useCurrentUser';

/**
 * Resolves the current location into the absolute URL the callback should return the browser to.
 *
 * The origin has to travel with the path. Each production frontend is served both at its
 * Terraform-set base URL (production.app.neo.tax) and at the shorter alias people actually use
 * (app.neo.tax), and the callback resolves a bare path against FRONTEND_BASE_URL — so an install
 * started at the alias came back on the canonical host. That is a different origin, which means a
 * different Firebase auth store: the user landed on an app they had to sign into again rather than
 * on the page they left.
 *
 * The install-status param is stripped so the page we ask the server to return us to is the page as
 * it was before the install started. Without this, re-installing from a page that already carries
 * the param would round-trip a stale status back into the return URL.
 */
export const getGitHubAppReturnUrl = ({ pathname, search }: { pathname: string; search: string }): string => {
  const url = new URL(pathname, window.location.origin);
  url.search = search;
  url.searchParams.delete(GitHubAppInstallQueryField.CONNECTED_STATUS);
  return url.toString();
};

/**
 * Starts the GitHub App install for the current company (PLAT-386). Mints a signed install URL on
 * app-service (opaque nonce as `state`) and performs a full-page redirect to GitHub. GitHub returns
 * the browser to the exact URL the install was started from, origin included, with
 * `?connected_github_app=pending`, where the slide-over re-opens and polls installationStatus (see
 * useGitHubAppInstallStatus). `error` is true when the URL couldn't be minted, so the caller can
 * surface a retry instead of leaving the user on a silently re-enabled button.
 */
export const useGitHubAppConnect = (): {
  install: (organizationSlug: string) => Promise<void>;
  loading: boolean;
  error: boolean;
} => {
  const companyId = useCurrentCompanyId();
  const { user } = useCurrentUser();
  const location = useLocation();
  const [error, setError] = useState(false);
  const [createInstallUrl, { loading }] = useMutation<
    CreateGitHubAppInstallUrlMutation,
    CreateGitHubAppInstallUrlMutationVariables
  >(CREATE_GITHUB_APP_INSTALL_URL);

  const install = async (organizationSlug: string): Promise<void> => {
    if (!user) {
      setError(true);
      return;
    }
    setError(false);
    try {
      const { data } = await createInstallUrl({
        variables: {
          userId: user.id,
          input: { companyId, organizationSlug, returnUrl: getGitHubAppReturnUrl(location) },
        },
      });
      const url = data?.createGitHubAppInstallUrl;
      if (url) {
        // Full-page redirect (GitHub's Setup URL flow leaves the app); the slide-over re-opens on return.
        window.location.assign(url);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  };

  return { install, loading, error };
};
